import { useState, type FormEvent } from "react";
import { Info, Minus, Plus, X } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { Match } from "effect";
import { decodeUnknownOption } from "../../../packages/common/src/schema.ts";
import { ApiError } from "#api.ts";
import { useAuth } from "#auth.tsx";
import { useLanguage } from "#i18n.tsx";
import { runEffectQuery } from "#effect-query.ts";
import type { Order } from "#orders.ts";
import {
  KitchenNotes,
  kitchenLabelKey,
  kitchenSettingsSchema,
  orderKitchenNotesSchema,
  defaultOrderQuickTags,
} from "../../../packages/common/src/schemas/kitchen-notes.ts";
import {
  kitchenLineLimit,
  type KitchenLine,
} from "../../../packages/common/src/schemas/kitchen-lines.ts";

/** Compact, non-interactive item annotations; the editor retains large tap targets. */
export function LineKitchenNotes({
  notes,
  limit,
}: {
  readonly notes: KitchenNotes;
  readonly limit: number;
}) {
  if (!notes.present) return null;
  return (
    <div
      className="line-kitchen-notes"
      role="group"
      aria-label="Item instructions"
    >
      <div className="instruction-tags">
        {notes.tags.map((tag) => (
          <span
            key={tag.label}
            className={`instruction-badge instruction-${tag.color}`}
          >
            {tag.label} <strong>× {tag.count}</strong>
          </span>
        ))}
      </div>
      {notes.text && (
        <span className="line-kitchen-note-text">{notes.text}</span>
      )}
      {notes.tags.some((tag) => tag.count > limit) && (
        <span className="line-instruction-warning" role="alert">
          Check instruction counts: the item quantity has changed.
        </span>
      )}
    </div>
  );
}

/** Order-wide text and preserved unmatched information, never guessed onto a line item. */
export function OrderKitchenNotes({ order }: { readonly order: Order }) {
  const { t } = useLanguage();
  const unmatched = order.unmatchedInstructions;
  if (
    !order.information &&
    unmatched.length === 0 &&
    order.quickTags.length === 0
  )
    return null;
  return (
    <section className="order-information" aria-label="Order information">
      <Info aria-hidden="true" />
      <div>
        {order.quickTags.length > 0 && (
          <div
            className="order-quick-tags"
            role="group"
            aria-label={t("notes.wholeOrder")}
          >
            <strong className="order-quick-tags-label">
              {t("notes.wholeOrder")}
            </strong>
            {order.quickTags.map((tag) => (
              <span
                key={tag.label}
                className={`instruction-badge instruction-${tag.color}`}
              >
                {tag.label === "Takeaway"
                  ? t("notes.takeaway")
                  : tag.label === "Clean before preparing (vegan)"
                    ? t("notes.cleanBefore")
                    : tag.label}
              </span>
            ))}
          </div>
        )}
        {order.information && <p>{order.information}</p>}
        {unmatched.map((note) => (
          <p className="unmatched-instructions" key={note.line_key}>
            Check instructions for a changed or removed item — {note.line_label}
            :{" "}
            {[
              ...note.tags.map((tag) => `${tag.label} × ${tag.count}`),
              note.text,
            ]
              .filter(Boolean)
              .join("; ")}
          </p>
        ))}
      </div>
    </section>
  );
}

/** Whole-order toggles and free text share a revision-protected save, separate from item instructions. */
export function OrderInformationEditor({
  order,
  pending,
  error,
  back,
  save,
}: {
  readonly order: Order;
  readonly pending: boolean;
  readonly error: string | undefined;
  readonly back: () => void;
  readonly save: (
    notes: typeof orderKitchenNotesSchema.Type,
    revision: string,
  ) => void;
}) {
  const { api } = useAuth();
  const { t } = useLanguage();
  const [base] = useState(order);
  const [text, setText] = useState(base.orderNotes.text);
  const [tags, setTags] = useState(base.quickTags);
  const [draftError, setDraftError] = useState<string>();
  const presetsQuery = useQuery({
    queryKey: ["merchants", base.merchantId, "kitchen-settings"],
    staleTime: 5 * 60_000,
    retry: false,
    queryFn: ({ signal }) =>
      runEffectQuery(
        api.requestEffect({
          path: `/merchants/${encodeURIComponent(base.merchantId)}/kitchen-settings`,
          schema: kitchenSettingsSchema,
          signal,
        }),
        signal,
      ),
  });
  const presets = [
    ...new Map(
      [
        ...defaultOrderQuickTags,
        ...(presetsQuery.data?.presets ?? []),
        ...tags,
      ].map((tag) => [kitchenLabelKey(tag.label), tag]),
    ).values(),
  ];
  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const parsed = decodeUnknownOption(orderKitchenNotesSchema, {
      tags: base.orderNotes.tags,
      quick_tags: tags,
      text,
    });

    if (!parsed) {
      setDraftError(t("notes.orderLimit"));
      return;
    }

    save(parsed, base.revision ?? "unversioned");
  };
  return (
    <form className="dialog-form" onSubmit={submit}>
      <div className="dialog-body">
        {(error || draftError) && (
          <p className="alert" role="alert">
            {error || draftError}
          </p>
        )}
        <p>{t("notes.wholeOrderHelp")}</p>
        <h3>{t("notes.wholeOrder")}</h3>
        <div
          className="instruction-presets"
          role="group"
          aria-label={t("notes.wholeOrder")}
        >
          {presets.map((preset) => {
            const selected = tags.some(
              (tag) =>
                kitchenLabelKey(tag.label) === kitchenLabelKey(preset.label),
            );
            return (
              <button
                type="button"
                key={preset.label}
                aria-pressed={selected}
                className={`instruction-preset instruction-${preset.color}`}
                disabled={pending || (!selected && tags.length >= 12)}
                onClick={() =>
                  setTags((current) =>
                    selected
                      ? current.filter(
                          (tag) =>
                            kitchenLabelKey(tag.label) !==
                            kitchenLabelKey(preset.label),
                        )
                      : [
                          ...current,
                          { label: preset.label, color: preset.color },
                        ],
                  )
                }
              >
                {selected ? (
                  <X aria-hidden="true" />
                ) : (
                  <Plus aria-hidden="true" />
                )}
                {preset.label === "Takeaway"
                  ? t("notes.takeaway")
                  : preset.label === "Clean before preparing (vegan)"
                    ? t("notes.cleanBefore")
                    : preset.label}
              </button>
            );
          })}
        </div>
        <p className="muted">{t("notes.cleanDisclaimer")}</p>
        {presetsQuery.isError && (
          <p role="status" className="muted">
            {t("notes.orderPresetsUnavailable")}
          </p>
        )}
        {base.orderNotes.tags.length > 0 && (
          <p className="muted">{t("notes.legacyPreserved")}</p>
        )}
        <label className="field">
          {t("notes.orderText")}
          <textarea
            rows={5}
            maxLength={1500}
            value={text}
            disabled={pending}
            onChange={(event) => setText(event.target.value)}
            placeholder={t("notes.orderPlaceholder")}
          />
        </label>
        <p className="muted">
          {t("notes.orderCharacters", { count: text.length })}
        </p>
      </div>
      <footer className="dialog-footer">
        <button
          className="secondary"
          type="button"
          disabled={pending}
          onClick={back}
        >
          {t("common.back")}
        </button>
        <button className="primary" type="submit" disabled={pending}>
          {pending ? t("notes.saving") : t("notes.saveOrder")}
        </button>
      </footer>
    </form>
  );
}

/** Item drafts retain their opening order revision even while realtime updates arrive. */
export function KitchenNotesEditor({
  order,
  line,
  pending,
  error,
  back,
  save,
}: {
  readonly order: Order;
  readonly line: KitchenLine;
  readonly pending: boolean;
  readonly error: string | undefined;
  readonly back: () => void;
  readonly save: (notes: KitchenNotes, revision: string) => void;
}) {
  const { api } = useAuth();
  const { t } = useLanguage();
  const [base] = useState(order);
  const [target] = useState(line);
  const [notes, setNotes] = useState(base.notesFor(target));
  const [text, setText] = useState(notes.text);
  const [draftError, setDraftError] = useState<string>();
  const limit = kitchenLineLimit(target);
  const presetsQuery = useQuery<typeof kitchenSettingsSchema.Type, ApiError>({
    queryKey: ["merchants", base.merchantId, "kitchen-settings"],
    retry: false,
    queryFn: ({ signal }) =>
      runEffectQuery(
        api.requestEffect({
          path: `/merchants/${encodeURIComponent(base.merchantId)}/kitchen-settings`,
          schema: kitchenSettingsSchema,
          signal,
        }),
        signal,
      ),
  });
  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const parsed = decodeUnknownOption(KitchenNotes.schema, {
      ...notes.toRecord(),
      text,
    });

    if (!parsed) {
      setDraftError(
        "Use up to 12 instructions and 500 characters of custom text.",
      );
      return;
    }

    if (parsed.tags.some((tag) => tag.count > limit)) {
      setDraftError(
        "Reduce instruction counts to match this item's ordered quantity.",
      );
      return;
    }

    save(parsed, base.revision ?? "unversioned");
  };
  return (
    <form className="dialog-form" onSubmit={submit}>
      <div className="dialog-body">
        {(error || draftError) && (
          <p className="alert" role="alert">
            {error || draftError}
          </p>
        )}
        <p>
          {target.quantity} ordered. Each tag counts how many of this item need
          that instruction. Tap again to increase its count.
        </p>
        {limit === 0 && (
          <p className="muted">
            This item does not have a whole-unit quantity. Use typed
            instructions instead of counted tags.
          </p>
        )}
        <h3>{t("notes.quick")}</h3>
        {Match.value(presetsQuery).pipe(
          Match.when({ status: "pending" }, () => (
            <p role="status">{t("notes.loading")}</p>
          )),
          Match.when({ status: "error" }, ({ error: queryError }) =>
            Match.valueTags(queryError, {
              ApiError: () => (
                <p className="alert" role="status">
                  Presets are unavailable. You can still edit existing
                  instructions or add custom text.
                </p>
              ),
            }),
          ),
          Match.when({ status: "success" }, ({ data }) => (
            <>
              {data.presets.length === 0 && (
                <p>
                  No presets saved. Add some in Settings, or use custom text
                  below.
                </p>
              )}
              <div className="instruction-presets">
                {data.presets.map((preset) => {
                  const selected = notes.tags.find(
                    (tag) =>
                      kitchenLabelKey(tag.label) ===
                      kitchenLabelKey(preset.label),
                  );
                  return (
                    <button
                      type="button"
                      key={preset.id}
                      className={`instruction-preset instruction-${preset.color}`}
                      aria-label={`Add ${preset.label}`}
                      disabled={
                        pending ||
                        (selected?.count ?? 0) >= limit ||
                        (!selected && notes.tags.length >= 12)
                      }
                      onClick={() =>
                        setNotes((value) => value.increment(preset))
                      }
                    >
                      <Plus aria-hidden="true" />
                      {preset.label}
                      {selected && <strong>× {selected.count}</strong>}
                    </button>
                  );
                })}
              </div>
            </>
          )),
          Match.exhaustive,
        )}
        {notes.tags.length > 0 && (
          <section
            className="selected-instructions"
            aria-label="Selected instructions"
          >
            <h3>{t("notes.forItem")}</h3>
            {notes.tags.map((tag) => (
              <div
                className="instruction-counter"
                key={tag.label}
                role="group"
                aria-label={`${tag.label} instruction`}
              >
                <span className={`instruction-badge instruction-${tag.color}`}>
                  {tag.label} <strong>× {tag.count}</strong>
                </span>
                <div className="instruction-counter-actions">
                  <button
                    className="secondary"
                    type="button"
                    disabled={pending}
                    aria-label={`Decrease ${tag.label}`}
                    onClick={() =>
                      setNotes((value) => value.decrement(tag.label))
                    }
                  >
                    <Minus />
                  </button>
                  <button
                    className="secondary"
                    type="button"
                    disabled={pending || tag.count >= limit}
                    aria-label={`Increase ${tag.label}`}
                    onClick={() => setNotes((value) => value.increment(tag))}
                  >
                    <Plus />
                  </button>
                  <button
                    className="secondary"
                    type="button"
                    disabled={pending}
                    aria-label={`Remove ${tag.label}`}
                    onClick={() => setNotes((value) => value.remove(tag.label))}
                  >
                    <X />
                  </button>
                </div>
              </div>
            ))}
          </section>
        )}
        <label className="field">
          Custom item instructions
          <textarea
            name="kitchenNotes"
            rows={4}
            maxLength={500}
            value={text}
            disabled={pending}
            onChange={(event) => setText(event.target.value)}
            placeholder="Instructions for this item only."
          />
        </label>
        <p className="muted">
          {text.length}/500 characters. Internal kitchen instructions only—not
          sent to Square or customer receipts. Presets can be managed in
          Settings.
        </p>
      </div>
      <footer className="dialog-footer">
        <button
          className="secondary"
          type="button"
          disabled={pending}
          onClick={back}
        >
          {t("common.back")}
        </button>
        <button className="primary" type="submit" disabled={pending}>
          {pending ? t("notes.saving") : t("notes.saveItem")}
        </button>
      </footer>
    </form>
  );
}
